import { LocalizedJoi } from "@/utils/localized-joi";
import dayjs from "dayjs";
import joiPhone from "joi-phone-number";
import { Loader } from "../ui/loader";
import { ContactInfoWrapper } from "./contact-info/contact-info-wrapper";
import { CustomerTypeForm } from "./customer-type-form";
import { MainCenterForm } from "./main-center-form";
import { AccountTypeForm } from "./medlemskap/account-type-form";
import { OrganizationWrapper } from "./organization/organization-wrapper";
import { BoxesLoader, SmallBoxesLoader } from "./skeletons";
import { SummaryWrapper } from "./summary/summary-wrapper";
import type { FormStep } from "./types";

export const BliMedlemSteps: Array<FormStep> = [
  {
    component: <CustomerTypeForm key="form-step-customer-type" />,
    id: "customerType",
    loader: <BoxesLoader />,
    shortTitle: "Alder",
    showDynamicZone: false,
    title: "Velg aldersgruppe",
    validatorSchema: LocalizedJoi.object({
      id: LocalizedJoi.number().required(),
    }).options({ allowUnknown: true }),
  },
  {
    component: <MainCenterForm key="form-step-main-center" />,
    id: "mainCenter",
    loader: <BoxesLoader />,
    shortTitle: "Senter",
    showDynamicZone: false,
    title: "Velg ditt favorittsenter",
    validatorSchema: LocalizedJoi.object({
      id: LocalizedJoi.number().required(),
    }).options({ allowUnknown: true }),
  },
  {
    component: <AccountTypeForm key="form-step-account-type" />,
    id: "accountType",
    loader: <BoxesLoader />,
    shortTitle: "Medlemskap",
    showDynamicZone: true,
    title: "Velg medlemskap",
    validatorSchema: LocalizedJoi.object({
      discountCode: LocalizedJoi.object({
        code: LocalizedJoi.string().allow(""),
        type: LocalizedJoi.any(),
      })
        .optional()
        .allow(null),
      id: LocalizedJoi.number().required(),
    }).options({ allowUnknown: true }),
  },
  {
    component: <ContactInfoWrapper key="form-step-user" />,
    id: "contactInfo",
    loader: <SmallBoxesLoader />,
    shortTitle: "Personalia",
    skipIfIsLoggedIn: true,
    title: "Personalia",
    validatorSchema: LocalizedJoi.object({
      // rekkefølge her bestemmer rekkefølge på validering/feilmeldinger i skjema
      /* eslint-disable sort-keys-fix/sort-keys-fix */
      firstName: LocalizedJoi.string().required().label("Fornavn"),
      lastName: LocalizedJoi.string().required().label("Etternavn"),
      email: LocalizedJoi.string()
        .required()
        .label("E-postadresse")
        .email({ tlds: { allow: false } }),
      birthdate: LocalizedJoi.date()
        .max(dayjs().add(-18, "years").toDate())
        .min(dayjs().add(-120, "years").toDate())
        .required()
        .label("Fødselsdato")
        .options({
          messages: {
            "date.max": `For medlemmer under 18 år, må en myndig person registreres som betaler. Kryss av i 'Skal du betale for noen andre?' lenger opp på siden.`,
            "date.min": "Fødselsår må være senere enn for 120 år siden.",
          },
        }),
      address: LocalizedJoi.string().required().label("Adresse"),
      postalCode: LocalizedJoi.string().required().label("Postnummer").length(4),
      postalAddress: LocalizedJoi.string().required().label("Poststed"),
      phone: LocalizedJoi.extend(joiPhone)
        .string()
        .phoneNumber({ defaultCountry: "NO", format: "international" })
        .required()
        .label("Telefonnummer"),
      gender: LocalizedJoi.string().allow("Mann", "Kvinne", "Ønsker ikke å oppgi").required().label("Kjønn"),
      onBehalfOf: LocalizedJoi.object({
        otherPayer: LocalizedJoi.boolean().required().label("Annen betaler"),
        firstName: LocalizedJoi.string()
          .label("Fornavn til medlem")
          .when("otherPayer", {
            is: true,
            // biome-ignore lint/suspicious/noThenProperty: <explanation>
            then: LocalizedJoi.required(),
            otherwise: LocalizedJoi.allow(""),
          }),
        lastName: LocalizedJoi.string()
          .label("Etternavn til medlem")
          .when("otherPayer", {
            is: true,
            // biome-ignore lint/suspicious/noThenProperty: <explanation>
            then: LocalizedJoi.required(),
            otherwise: LocalizedJoi.allow(""),
          }),
        address: LocalizedJoi.string()
          .label("Adresse til medlem")
          .when("otherPayer", {
            is: true,
            // biome-ignore lint/suspicious/noThenProperty: <explanation>
            then: LocalizedJoi.required(),
            otherwise: LocalizedJoi.allow(""),
          }),
        postalCode: LocalizedJoi.string()
          .label("Postnummer til medlem")
          .length(4)
          .when("otherPayer", {
            is: true,
            // biome-ignore lint/suspicious/noThenProperty: <explanation>
            then: LocalizedJoi.required(),
            otherwise: LocalizedJoi.allow(""),
          }),
        postalAddress: LocalizedJoi.string()
          .label("Poststed til medlem")
          .when("otherPayer", {
            is: true,
            // biome-ignore lint/suspicious/noThenProperty: <explanation>
            then: LocalizedJoi.required(),
            otherwise: LocalizedJoi.allow(""),
          }),
        phone: LocalizedJoi.extend(joiPhone)
          .string()
          .label("Telefonnummer til medlem")
          .phoneNumber({ defaultCountry: "NO", format: "international" })
          .when("otherPayer", {
            is: true,
            // biome-ignore lint/suspicious/noThenProperty: <explanation>
            then: LocalizedJoi.required(),
            otherwise: LocalizedJoi.allow(""),
          }),
        email: LocalizedJoi.string()
          .email({ tlds: { allow: false } })
          .label("E-postadresse til medlem")
          .when("otherPayer", {
            is: true,
            // biome-ignore lint/suspicious/noThenProperty: <explanation>
            then: LocalizedJoi.required(),
            otherwise: LocalizedJoi.allow(""),
          }),
        birthdate: LocalizedJoi.date()
          .min(dayjs().add(-120, "years").toDate())
          .label("Fødselsdato til medlem")
          .when("otherPayer", {
            is: true,
            // biome-ignore lint/suspicious/noThenProperty: <explanation>
            then: LocalizedJoi.required().options({
              messages: {
                "date.min": "Fødselsår må være senere enn for 120 år siden.",
              },
            }),
            otherwise: LocalizedJoi.allow(""),
          }),
        gender: LocalizedJoi.string()
          .allow("Mann", "Kvinne", "Ønsker ikke å oppgi")
          .label("Kjønn medlem")
          .when("otherPayer", {
            is: true,
            // biome-ignore lint/suspicious/noThenProperty: <explanation>
            then: LocalizedJoi.required(),
            otherwise: LocalizedJoi.allow(""),
          }),
      }),
      /* eslint-enable sort-keys-fix/sort-keys-fix */
    }).options({ allowUnknown: true }),
  },
  {
    component: <SummaryWrapper key="form-step-summary" />,
    hideSubTitle: true,
    id: "summary",
    loader: <Loader />,
    notEditableInStepIndicator: true,
    shortTitle: "Oppsummering",
    title: "Oppsummering",
    validatorSchema: LocalizedJoi.object({
      termsAndConditions: LocalizedJoi.boolean()
        .required()
        .equal(true)
        .label("Godta vilkår")
        .messages({ "any.only": "Du må godta vilkårene" }),
      termsMarketing: LocalizedJoi.boolean(),
    }),
  },
];

export const BedriftSteps: Array<FormStep> = [
  {
    component: <CustomerTypeForm key="form-step-customer-type" />,
    id: "customerType",
    loader: <BoxesLoader />,
    shortTitle: "Alder",
    showDynamicZone: false,
    title: "Velg aldersgruppe",
    validatorSchema: LocalizedJoi.object({
      id: LocalizedJoi.number().required(),
    }).options({ allowUnknown: true }),
  },
  {
    component: <OrganizationWrapper key="form-step-organization" />,
    id: "organization",
    loader: <BoxesLoader />,
    shortTitle: "Bedrift",
    showDynamicZone: false,
    title: "Bedriftsmedlemskap",
    validatorSchema: LocalizedJoi.object({
      id: LocalizedJoi.number().required(),
    }).options({ allowUnknown: true }),
  },
  {
    component: <AccountTypeForm key="form-step-account-type" />,
    id: "accountType",
    loader: <BoxesLoader />,
    shortTitle: "Medlemskap",
    showDynamicZone: true,
    title: "Velg medlemskap",
    validatorSchema: LocalizedJoi.object({
      discountCode: LocalizedJoi.object({
        code: LocalizedJoi.string().allow(""),
        type: LocalizedJoi.any(),
      })
        .optional()
        .allow(null),
      id: LocalizedJoi.number().required(),
    }).options({ allowUnknown: true }),
  },
  {
    component: <ContactInfoWrapper key="form-step-user" />,
    id: "contactInfo",
    loader: <SmallBoxesLoader />,
    shortTitle: "Personalia",
    skipIfIsLoggedIn: true,
    title: "Personalia",
    validatorSchema: LocalizedJoi.object({
      // rekkefølge her bestemmer rekkefølge på validering/feilmeldinger i skjema
      /* eslint-disable sort-keys-fix/sort-keys-fix */
      firstName: LocalizedJoi.string().required().label("Fornavn"),
      lastName: LocalizedJoi.string().required().label("Etternavn"),
      email: LocalizedJoi.string()
        .required()
        .label("E-postadresse")
        .email({ tlds: { allow: false } }),
      birthdate: LocalizedJoi.date()
        .max(dayjs().add(-18, "years").toDate())
        .min(dayjs().add(-120, "years").toDate())
        .required()
        .label("Fødselsdato")
        .options({
          messages: {
            "date.max": `For medlemmer under 18 år, må en myndig person registreres som betaler. Kryss av i 'Skal du betale for noen andre?' lenger opp på siden.`,
            "date.min": "Fødselsår må være senere enn for 120 år siden.",
          },
        }),
      address: LocalizedJoi.string().required().label("Adresse"),
      postalCode: LocalizedJoi.string().required().label("Postnummer").length(4),
      postalAddress: LocalizedJoi.string().required().label("Poststed"),
      phone: LocalizedJoi.extend(joiPhone)
        .string()
        .phoneNumber({ defaultCountry: "NO", format: "international" })
        .required()
        .label("Telefonnummer"),
      gender: LocalizedJoi.string().allow("Mann", "Kvinne", "Ønsker ikke å oppgi").required().label("Kjønn"),
      onBehalfOf: LocalizedJoi.object({
        otherPayer: LocalizedJoi.boolean().required().label("Annen betaler"),
        firstName: LocalizedJoi.string()
          .label("Fornavn til medlem")
          .when("otherPayer", {
            is: true,
            // biome-ignore lint/suspicious/noThenProperty: <explanation>
            then: LocalizedJoi.required(),
            otherwise: LocalizedJoi.allow(""),
          }),
        lastName: LocalizedJoi.string()
          .label("Etternavn til medlem")
          .when("otherPayer", {
            is: true,
            // biome-ignore lint/suspicious/noThenProperty: <explanation>
            then: LocalizedJoi.required(),
            otherwise: LocalizedJoi.allow(""),
          }),
        address: LocalizedJoi.string()
          .label("Adresse til medlem")
          .when("otherPayer", {
            is: true,
            // biome-ignore lint/suspicious/noThenProperty: <explanation>
            then: LocalizedJoi.required(),
            otherwise: LocalizedJoi.allow(""),
          }),
        postalCode: LocalizedJoi.string()
          .label("Postnummer til medlem")
          .length(4)
          .when("otherPayer", {
            is: true,
            // biome-ignore lint/suspicious/noThenProperty: <explanation>
            then: LocalizedJoi.required(),
            otherwise: LocalizedJoi.allow(""),
          }),
        postalAddress: LocalizedJoi.string()
          .label("Poststed til medlem")
          .when("otherPayer", {
            is: true,
            // biome-ignore lint/suspicious/noThenProperty: <explanation>
            then: LocalizedJoi.required(),
            otherwise: LocalizedJoi.allow(""),
          }),
        phone: LocalizedJoi.extend(joiPhone)
          .string()
          .label("Telefonnummer til medlem")
          .phoneNumber({ defaultCountry: "NO", format: "international" })
          .when("otherPayer", {
            is: true,
            // biome-ignore lint/suspicious/noThenProperty: <explanation>
            then: LocalizedJoi.required(),
            otherwise: LocalizedJoi.allow(""),
          }),
        email: LocalizedJoi.string()
          .email({ tlds: { allow: false } })
          .label("E-postadresse til medlem")
          .when("otherPayer", {
            is: true,
            // biome-ignore lint/suspicious/noThenProperty: <explanation>
            then: LocalizedJoi.required(),
            otherwise: LocalizedJoi.allow(""),
          }),
        birthdate: LocalizedJoi.date()
          .min(dayjs().add(-120, "years").toDate())
          .label("Fødselsdato til medlem")
          .when("otherPayer", {
            is: true,
            // biome-ignore lint/suspicious/noThenProperty: <explanation>
            then: LocalizedJoi.required().options({
              messages: {
                "date.min": "Fødselsår må være senere enn for 120 år siden.",
              },
            }),
            otherwise: LocalizedJoi.allow(""),
          }),
        gender: LocalizedJoi.string()
          .allow("Mann", "Kvinne", "Ønsker ikke å oppgi")
          .label("Kjønn medlem")
          .when("otherPayer", {
            is: true,
            // biome-ignore lint/suspicious/noThenProperty: <explanation>
            then: LocalizedJoi.required(),
            otherwise: LocalizedJoi.allow(""),
          }),
      }),
      /* eslint-enable sort-keys-fix/sort-keys-fix */
    }).options({ allowUnknown: true }),
  },
  {
    component: <SummaryWrapper key="form-step-summary" />,
    hideSubTitle: true,
    id: "summary",
    loader: <Loader />,
    notEditableInStepIndicator: true,
    shortTitle: "Oppsummering",
    title: "Oppsummering",
    validatorSchema: LocalizedJoi.object({
      termsAndConditions: LocalizedJoi.boolean()
        .required()
        .equal(true)
        .label("Godta vilkår")
        .messages({ "any.only": "Du må godta vilkårene" }),
      termsMarketing: LocalizedJoi.boolean(),
    }),
  },
];
